import { format } from 'date-fns';
import { Loader2, GitBranch } from 'lucide-react';
import { useCandidateStageHistoryV2 } from '@/hooks/useCandidatesV2';

const STAGE_LABELS = {
  applied: 'Applied', resume_parsed: 'Resume Parsed', eligible: 'Eligible',
  exam_assigned: 'Exam Assigned', exam_completed: 'Exam Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  offer: 'Offer', hired: 'Hired', rejected: 'Rejected',
};

const STAGE_COLORS = {
  applied: 'bg-gray-100 text-gray-700',
  resume_parsed: 'bg-blue-100 text-blue-700',
  eligible: 'bg-teal-100 text-teal-700',
  exam_assigned: 'bg-purple-100 text-purple-700',
  exam_completed: 'bg-indigo-100 text-indigo-700',
  shortlisted: 'bg-orange-100 text-orange-700',
  interview_scheduled: 'bg-yellow-100 text-yellow-700',
  offer: 'bg-green-100 text-green-700',
  hired: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const StageBadge = ({ stage }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[stage] || 'bg-gray-100 text-gray-600'}`}>
    {STAGE_LABELS[stage] || stage}
  </span>
);

const CandidateStageHistoryTabV2 = ({ candidateId }) => {
  const { data, isLoading } = useCandidateStageHistoryV2(candidateId);
  const history = data?.history || data || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading history...
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <GitBranch className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">No stage changes recorded</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((entry) => (
        <div key={entry._id} className="flex gap-3 text-sm">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
            <div className="w-px flex-1 bg-gray-200 mt-1" />
          </div>
          <div className="pb-3 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {entry.fromStage && <StageBadge stage={entry.fromStage} />}
              {entry.fromStage && <span className="text-gray-400 text-xs">→</span>}
              <StageBadge stage={entry.toStage} />
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
              {entry.changedBy && (
                <span>by {entry.changedBy.name || entry.changedBy.email || 'Unknown'}</span>
              )}
              {entry.changedAt && (
                <span>{format(new Date(entry.changedAt), 'MMM dd, yyyy HH:mm')}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CandidateStageHistoryTabV2;
